import type { PlatformChannel } from './map-connection-to-row';
import type { SolutionResolver } from './resolve-solution-by-bot-id';

/** Channels shown in a solution box before "See N more" takes over. */
export const MAX_VISIBLE_CHANNELS_PER_SOLUTION = 3;

/**
 * The slice of a project solution this grouping reads. `ctx.solutions` entries
 * satisfy it. Which solution a channel belongs to is decided by
 * `createSolutionResolver` — the same resolver `useGoToChannel` uses.
 */
export interface GroupableSolution {
  /** Optional in the generated type; a solution without an id has no route. */
  id?: string;
  botSolutionId?: string | null;
  displayName?: string | null;
  name?: string | null;
}

export interface SolutionChannelGroup {
  /** The solution's route id, or `null` for the "No solution" box. */
  solutionId: string | null;
  /**
   * Display name, or `null` for the "No solution" box. The heading of that box
   * is user-facing text, so it is the renderer's to translate: this helper stays
   * free of i18n and only says WHICH box it is.
   */
  solutionName: string | null;
  /** Channels of this box, A-Z. */
  channels: PlatformChannel[];
  /**
   * `true` for the single group produced while the solutions are NOT KNOWN
   * (no resolver): every channel, A-Z, in no particular solution. Not the
   * "No solution" box — that one says the solutions are known and none owns
   * these channels — so renderers draw it with no region, no chip and no
   * "No solution" heading.
   */
  unresolved?: true;
}

// Same collation for both sorts: case-insensitive, accent-insensitive, numeric
// runs compared as numbers so "Channel 2" precedes "Channel 10". The locale is
// PINNED, as `PLATFORM_LABEL_COLLATOR` does and for its reason: an `undefined`
// locale follows the machine, so the order would differ between a browser and
// the Node that computes a test's expectation.
const COLLATOR = new Intl.Collator('en', {
  sensitivity: 'base',
  numeric: true,
});

// Tie-break on ids by UTF-16 code units — no locale at all, so it is the same
// everywhere. `localeCompare` without arguments would use the default locale.
const compareIds = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

const byName = (a: PlatformChannel, b: PlatformChannel) =>
  COLLATOR.compare(a.name, b.name) || compareIds(a.channelId, b.channelId);

/**
 * Groups a connection's channels into one box per solution.
 *
 * With NO resolver (the solutions are not known yet, see
 * `createLoadedSolutionResolver`) it returns one `unresolved` group holding
 * every channel A-Z, or nothing for no channels. Nothing is assigned to
 * "No solution" then: that would claim a fact we do not have.
 *
 * Boxes are ordered A-Z by solution name and channels A-Z inside a box. A
 * channel whose `botId` resolves to no solution (absent, a solution the project
 * list does not hold, or one without an id) goes into a single "No solution"
 * box that is always last, whatever the real names sort like. Boxes are keyed
 * by solution id, so two solutions with one display name stay two boxes
 * (ordered by id, to be stable).
 *
 * Takes a RESOLVER rather than the solution list so the caller builds the
 * index once per `solutions` and not once per row.
 *
 * Pure: the table (Lit) and the Edit modal (React) both render this one result.
 */
export const groupChannelsBySolution = (
  channels: PlatformChannel[],
  resolveSolution: SolutionResolver<GroupableSolution> | null
): SolutionChannelGroup[] => {
  if (!resolveSolution) {
    if (channels.length === 0) return [];
    return [
      {
        solutionId: null,
        solutionName: null,
        unresolved: true,
        channels: [...channels].sort(byName),
      },
    ];
  }

  const bySolutionId = new Map<string, SolutionChannelGroup>();
  const unassigned: PlatformChannel[] = [];

  for (const channel of channels) {
    const solution = resolveSolution(channel.botId);
    const solutionId = solution?.id;
    if (!solution || !solutionId) {
      unassigned.push(channel);
      continue;
    }
    const existing = bySolutionId.get(solutionId);
    if (existing) {
      existing.channels.push(channel);
    } else {
      bySolutionId.set(solutionId, {
        solutionId,
        solutionName:
          solution.displayName || solution.name || solution.botSolutionId || '',
        channels: [channel],
      });
    }
  }

  const groups = [...bySolutionId.values()].sort(
    (a, b) =>
      COLLATOR.compare(a.solutionName ?? '', b.solutionName ?? '') ||
      compareIds(a.solutionId ?? '', b.solutionId ?? '')
  );
  for (const group of groups) group.channels.sort(byName);

  if (unassigned.length > 0) {
    groups.push({
      solutionId: null,
      solutionName: null,
      channels: unassigned.sort(byName),
    });
  }
  return groups;
};

/**
 * Which channels of a box are drawn. Collapsed shows the first
 * `MAX_VISIBLE_CHANNELS_PER_SOLUTION`; `remaining` is the overflow count either
 * way, so the toggle can read "See N more" / "See less" from one value.
 */
export const splitVisibleChannels = (
  channels: PlatformChannel[],
  expanded: boolean
): { visible: PlatformChannel[]; remaining: number } => ({
  visible: expanded
    ? channels
    : channels.slice(0, MAX_VISIBLE_CHANNELS_PER_SOLUTION),
  remaining: Math.max(0, channels.length - MAX_VISIBLE_CHANNELS_PER_SOLUTION),
});
